import { HttpClient } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { Entry } from 'contentful';
import { RevisionNoticesPage } from 'src/app/model/page/group/revision-notices/revision-notices-page';
import { TableSection } from 'src/app/model/shared/table-section';
import { ContentfulService } from 'src/app/service/contentful.service';
import { SharedDataService } from 'src/app/service/shared-data.service';
import { ModelValidator } from 'src/app/validator/model-validator';
import htmlTitle from 'src/assets/json/redirects/html-title.json';

@Component({
  selector: 'app-revision-notices-page',
  templateUrl: './revision-notices-page.component.html',
  styleUrls: ['./revision-notices-page.component.scss']
})
export class RevisionNoticesPageComponent implements OnInit {
  @Input() pageTitle = '';

  revisionNoticesPage: RevisionNoticesPage = {} as RevisionNoticesPage;
  isFareBandNameAndCodeOrderReshopPage: boolean = false;
  table: TableSection[] = [];

  constructor(private contentfulService: ContentfulService, private sharedDataService: SharedDataService, private httpClient: HttpClient) {}

  ngOnInit(): void {
    htmlTitle.filter((title) => {
      if (title.page === 'kpi-definition') {
        this.revisionNoticesPage.htmlTitle = title.htmlTitle;
      }
    });
    this.revisionNoticesPage.productChangeNoticeSections = [];
    this.revisionNoticesPage.abstractSections = [];
    this.revisionNoticesPage.apiFlowSections = [];
    this.revisionNoticesPage.detailsXMLSections = [];
    this.revisionNoticesPage.technicalReferencesSections = [];
    this.revisionNoticesPage.technicalReferencesXMLModalSections = [];
    this.revisionNoticesPage.xmlSample = [];

    const query = { 'fields.title': this.pageTitle.split('RN-').pop() };

    this.isFareBandNameAndCodeOrderReshopPage = query['fields.title'] === 'Fare Band Name and Code - OrderReshop';

    this.contentfulService.getPageContentResponse(query).then((revisionNoticesPageEntries) => {
      this.mapBanners(revisionNoticesPageEntries.includes.Entry);
      this.mapHtmlTitle(revisionNoticesPageEntries.items);
      this.sharedDataService.addSharedData(this.revisionNoticesPage.banner);
      this.mapTableSections(revisionNoticesPageEntries.includes.Entry);
      this.mapSections(revisionNoticesPageEntries.includes.Entry);
      this.mapNoteSections(revisionNoticesPageEntries.includes.Entry);
      this.revisionNoticesPage.productChangeNoticeSections.sort((a, b) => a.order - b.order);
      this.revisionNoticesPage.apiFlowSections.sort((a, b) => a.order - b.order);
      this.revisionNoticesPage.technicalReferencesSections.sort((a, b) => a.order - b.order);
      this.revisionNoticesPage.technicalReferencesXMLModalSections.sort((a, b) => a.order - b.order);
    });
  }

  private mapHtmlTitle(items: Entry<any>[]) {
    items.forEach((entry: any) => {
      let editorDetails = entry.fields?.editorDetails?.split('#');
      this.revisionNoticesPage.htmlTitle = entry.fields.htmlTitle;
      this.revisionNoticesPage.banner.lastUpdated = entry.fields?.lastUpdated;
      this.revisionNoticesPage.banner.url = entry.fields?.url;
      this.revisionNoticesPage.banner.editorEmail = editorDetails?.pop();
      this.revisionNoticesPage.banner.editorShortName = editorDetails?.pop();
      this.revisionNoticesPage.banner.editorFullName = editorDetails?.pop();
      this.revisionNoticesPage.htmlTitle = entry.fields.htmlTitle;
    });
  }

  private mapBanners(revisionNoticesPageEntries: Entry<any>[]) {
    revisionNoticesPageEntries
      .filter((entry) => ModelValidator.isBanner(entry))
      .forEach((entry) => {
        this.mapEntryToBanner(entry);
      });
  }

  private mapEntryToBanner(entry: Entry<any>) {
    let image: any = entry.fields['image'] as string;
    let video: any = entry.fields['video'] as string;
    this.revisionNoticesPage.banner = {
      title: entry.fields['title'] as string,
      subTitle: entry.fields['subTitle'] as string,
      image: image.fields.file.url,
      video: video?.fields?.file?.url
    };
  }

  private mapSections(revisionNoticesPageEntries: Entry<any>[]) {
    (revisionNoticesPageEntries || [])
      .filter((entry) => ModelValidator.isSection(entry))
      .forEach((entry) => {
        switch (entry.fields['group']) {
          case 'abstract':
            this.mapAbstractSections(entry);
            break;
          case 'affected-api-flow':
            this.mapAPIFlow(entry);
            break;
          case 'details':
            this.mapDetailsSection(entry);
            break;
          case 'technical-references':
            this.mapTechnicalReferencesSection(entry);
            break;
          case 'technical-references-xml-modal':
            this.mapTechnicalReferencesXMLModalSection(entry);
            break;
          case 'xml-sample':
            this.mapXMLSample(entry);
            break;
          default:
            // console.log('found a different section group');
            break;
        }
      });
  }

  private mapNoteSections(revisionNoticesPageEntries: Entry<any>[]) {
    (revisionNoticesPageEntries || [])
      .filter((entry) => ModelValidator.isNote(entry))
      .forEach((entry) => {
        switch (entry.fields['group']) {
          case 'note-within-abstract':
            this.mapAbstractNoteSections(entry);
            break;
          case 'note-within-details':
            this.mapDetailsXMLNoteSections(entry);
            break;
          default:
            // console.log('cannot find a group name');
            break;
        }
      });
  }

  private mapAbstractNoteSections(entry: Entry<any>) {
    this.revisionNoticesPage.abstractSections.forEach((abstractSection) => {
      let pageTitle = entry.fields['title'] as string;
      const noteTitle = pageTitle.split('#');

      noteTitle.pop();
      if (noteTitle.pop() === abstractSection.title) {
        let givenTitle: any = entry.fields['title'] as string;
        let pageTitle = givenTitle.split('#').pop();

        abstractSection.noteSections?.push({
          title: pageTitle,
          body: entry.fields['body'] as string,
          group: entry.fields['group'] as string,
          category: entry.fields['category'] as string,
          order: entry.fields['order'] as number,
          fullTitle: entry.fields['title'] as string,
          iconClass: entry.fields['iconClass'] as string,
          noteClass: entry.fields['noteClass'] as string
        });
        abstractSection.noteSections?.sort((a, b) => a.order - b.order);
      }
    });
  }

  private mapDetailsXMLNoteSections(entry: Entry<any>) {
    this.revisionNoticesPage.detailsXMLSections.forEach((detailsXMLSection) => {
      let pageTitle = entry.fields['title'] as string;

      const noteTitle = pageTitle.split('#');
      noteTitle.pop();
      if (noteTitle.pop() === detailsXMLSection.title) {
        let givenTitle: any = entry.fields['title'] as string;
        let pageTitle = givenTitle.split('#').pop();

        detailsXMLSection.noteSections?.push({
          title: pageTitle,
          body: entry.fields['body'] as string,
          group: entry.fields['group'] as string,
          category: entry.fields['category'] as string,
          order: entry.fields['order'] as number,
          fullTitle: entry.fields['title'] as string,
          iconClass: entry.fields['iconClass'] as string,
          noteClass: entry.fields['noteClass'] as string
        });
        detailsXMLSection.noteSections?.sort((a, b) => a.order - b.order);
      }
    });
  }

  private mapAbstractSections(entry: Entry<any>) {
    let givenTitle: any = entry.fields['title'] as string;
    let pageTitle = givenTitle.split('#').pop();

    this.revisionNoticesPage.abstractSections.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number,
      noteSections: []
    });
  }

  private mapTableSections(revisionNoticesPageEntries: Entry<any>[]) {
    (revisionNoticesPageEntries || [])
      .filter((entry) => ModelValidator.isTableSection(entry))
      .forEach((entry) => {
        switch (entry.fields['group']) {
          case 'product-change-note':
            this.mapProductChangeNoteSections(entry);
            break;
          case 'table-within-details':
            this.mapDetailsSectionTables(entry);
            break;
          default:
          // console.log('error in getting section');
        }
      });
  }

  private mapProductChangeNoteSections(entry: Entry<any>) {
    let thead: any[] = [];
    let tbody: any[] = [];

    let givenContent: any = entry.fields['table'];

    let tableContent = givenContent.content;

    tableContent.forEach((data: any) => {
      if (data.nodeType == 'table') {
        data.content.forEach((tableRow: any) => {
          if (tableRow.nodeType === 'table-row') {
            tableRow.content.forEach((tableItem: any) => {
              if (tableItem.nodeType === 'table-header-cell') {
                thead.push(tableItem.content[0].content[0].value);
              } else if (tableItem.nodeType === 'table-cell') {
                tbody.push(tableItem.content[0].content[0].value);
              }
            });
          }
        });
      }
    });

    let givenTitle: any = entry.fields['title'] as string;
    let pageTitle = givenTitle.split('#').pop();

    this.revisionNoticesPage.productChangeNoticeSections.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number,
      tHead: thead,
      tBody: tbody
    });
  }

  private mapAPIFlow(entry: Entry<any>) {
    let tBody: string[] = [];

    let pageBody: any = entry.fields['body'] as string;

    pageBody.split('\n').forEach((apiArrow: any) => {
      if (apiArrow !== '') {
        tBody.push(apiArrow);
      }
    });

    let givenTitle: any = entry.fields['title'] as string;
    let pageTitle = givenTitle.split('#').pop();

    this.revisionNoticesPage.apiFlowSections.push({
      title: pageTitle,
      body: '',
      bodyArrows: tBody,
      order: entry.fields['order'] as number,
      group: entry.fields['group'] as string,
      heading: entry.fields['heading'] as string
    });
  }

  private mapDetailsSection(entry: Entry<any>) {
    let detailsXMLArray: string[] = [];

    if(typeof entry.fields['xml']!=='undefined'){
    let givenXML: any = entry.fields['xml'];
    givenXML.content.forEach((element: any) => {
      this.httpClient.get(element?.data?.target?.fields?.file.url, { responseType: 'text' }).subscribe({
        next: (data: string) => detailsXMLArray.push(data),
        error: () => detailsXMLArray.push()
      });
    });
  }
    this.mapDetailsSectionWithXMLData(entry, detailsXMLArray);
  }

  private mapDetailsSectionWithXMLData(entry: Entry<any>, xmlData: string[]) {
    let givenTitle: any = entry.fields['title'] as string;
    let pageTitle = givenTitle.split('#').pop();

    this.revisionNoticesPage.detailsXMLSections.push({
      title: pageTitle,
      order: entry.fields['order'] as number,
      body: entry.fields['body'] as string,
      subBody: entry.fields['subBody'] as string,
      xmlDataArray: xmlData,
      group: entry.fields['group'] as string,
      tableSections: [],
      noteSections: []
    });

    const arrLength = this.revisionNoticesPage.detailsXMLSections.length;
    this.table.forEach((singleTable) => {
      let givenTitle = entry.fields['title'] as string;
      if (givenTitle.split('#').pop() == singleTable.tableCategory) {
        this.revisionNoticesPage.detailsXMLSections[arrLength - 1].tableSections!.push(singleTable);
      }
    });

    this.revisionNoticesPage.detailsXMLSections.forEach((detailsXMLSection) => {
      detailsXMLSection.tableSections?.sort((a, b) => a.order - b.order);
    });

    this.revisionNoticesPage.detailsXMLSections.sort((a, b) => a.order - b.order);
  }

  private mapTechnicalReferencesSection(entry: Entry<any>) {
    let technicalReferencesXMLArray: any[] = [];


    if(typeof entry.fields['xml']!=='undefined'){
    let givenXML:any= entry.fields['xml'];

    givenXML.content.forEach((element: any) => {
      let xmlTitle = element?.data?.target?.fields?.title.split('#').pop();
      this.httpClient.get(element?.data?.target?.fields?.file.url, { responseType: 'text' }).subscribe({
        next: (data: any) => technicalReferencesXMLArray.push({ xmlData: data, xmlTitle: xmlTitle }),
        error: () => technicalReferencesXMLArray.push()
      });
    });
  }
    this.mapTechnicalReferencesSectionWithXMLData(entry, technicalReferencesXMLArray);
  }

  private mapTechnicalReferencesXMLModalSection(entry: Entry<any>) {
    let givenTitle:any=entry.fields['title'] as string;
    let pageTitle=givenTitle.split('#').pop();

    this.revisionNoticesPage.technicalReferencesXMLModalSections.push({
      title:pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number
    });
  }

  private mapTechnicalReferencesSectionWithXMLData(entry: Entry<any>, xmlData: any[]) {
    let givenTitle: any = entry.fields['title'] as string;
    let pageTitle = givenTitle.split('#').pop();

    this.revisionNoticesPage.technicalReferencesSections.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number,
      xmlDataArray: xmlData
    });
  }

  private mapDetailsSectionTables(entry: Entry<any>) {
    let givenTitle: any = entry.fields['title'] as string;
    let pageTitle = givenTitle.split('#')[2];

    let giventableCategory = entry.fields['title'] as string;

    this.table.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      order: entry.fields['order'] as number,
      group: entry.fields['group'] as string,
      table: entry.fields['table'] as string,
      tableCategory: giventableCategory.split('#')[1]
    });
  }

  private mapXMLSample(entry: Entry<any>) {
    if(typeof entry.fields['xml']!=='undefined'){
    let xmlPath:any=entry.fields['xml'];

    this.httpClient.get(xmlPath.content[0].data.target.fields.file.url, { responseType: 'text' }).subscribe({
      next: (data: string) => this.mapXMLSampleWithXMLData(entry, data),
      error: () => this.mapXMLSampleWithXMLData(entry, '')
    });
  }}

  private mapXMLSampleWithXMLData(entry: Entry<any>, xmlData: string) {
    let givenTitle: any = entry.fields['title'] as string;
    let pageTitle = givenTitle.split('#').pop();

    this.revisionNoticesPage.xmlSample.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number,
      xmlData: xmlData
    });
  }
}
